// Use this to access our auto-generated API
// To update the API:
// 1) run 'make generate_openapi_spec' in the backend folder
// 2) run 'npm run update-api-client' in the frontend folder

import { LxConfig } from 'global/constants';
import {
    AnalyticsApi as _AnalyticsApi,
    BannersApi as _BannersApi,
    BooksApi as _BooksApi,
    ClassroomsApi as _ClassroomsApi,
    ClustersApi as _ClustersApi,
    Configuration,
    DiscussionsApi as _DiscussionsApi,
    ExploreApi as _ExploreApi,
    FavoritesApi as _FavoritesApi,
    ItemsApi as _ItemsApi,
    LearningApi as _LearningApi,
    LibraryApi as _LibraryApi,
    MentorshipsApi as _MentorshipsApi,
    ModerationApi as _ModerationApi,
    NotificationsApi as _NotificationsApi,
    OrganizationsApi as _OrganizationsApi,
    SearchApi as _SearchApi,
    RedirectsApi as _RedirectsApi,
    UsersApi as _UsersApi,
    XblocksApi as _XblocksApi,
} from 'labxchange-client';
import { getSavedLanguage } from 'i18n/index';
import { getStore } from 'global/store';
import { markUserAsLoggedOut } from 'auth/actions';

const getOAuthToken = () => localStorage.getItem('auth_token');

const config = new Configuration({
    accessToken: (name: string, scopes?: string[]) => {
        const authToken = getOAuthToken();
        return authToken ? `Bearer ${authToken}` : '';
    },
    basePath: `${LxConfig.BackendBaseUrl}/api/v1`,
    middleware: [
        {
            pre: async (context) => {
                // send the preferred language in the request for i18n
                const lang = getSavedLanguage();
                const headers = new Headers(context.init.headers);
                headers.set('labxchange-language', lang);
                context.init.headers = headers;

                // If the user is anonymous (not logged in), we may need to send a
                // session ID header so that there is a way to identify them.
                const authToken = getOAuthToken();
                if (!authToken) {
                    // This user is not logged in. Send a session ID header, if we have one.
                    const sessionToken = localStorage.getItem('anon_lx_session_token');
                    if (sessionToken) {
                        const newHeaders = new Headers(context.init.headers);
                        newHeaders.set('labxchange-session', sessionToken);
                        context.init.headers = newHeaders;
                    }
                }
                return context;
            },
            post: async (context) => {
                // If the user is not logged in, the response may include a new session token
                // that we must save into localStorage to authenticate this anonymous user for
                // future API calls:
                const newAnonSessionToken = context.response.headers.get('labxchange-session');
                if (newAnonSessionToken !== null) {
                    localStorage.setItem('anon_lx_session_token', newAnonSessionToken);
                } else if (getOAuthToken()) {
                    // This user has now logged in - discard the old anonymous session
                    localStorage.removeItem('anon_lx_session_token');
                }

                // Ensure that we update the logged in status as the earliest possible opportunity,
                // ie. here as soon as we see a 401.
                if (context.response.status === 401) {
                    markUserAsLoggedOut(getStore().dispatch);
                }

                return context.response;
            },
        },
    ],
});

export const AnalyticsApi = new _AnalyticsApi(config);
export const BannersApi = new _BannersApi(config);
export const BooksApi = new _BooksApi(config);
export const ClassroomsApi = new _ClassroomsApi(config);
export const ClustersApi = new _ClustersApi(config);
export const DiscussionsApi = new _DiscussionsApi(config);
export const ExploreApi = new _ExploreApi(config);
export const FavoritesApi = new _FavoritesApi(config);
export const ItemsApi = new _ItemsApi(config);
export const LearningApi = new _LearningApi(config);
export const LibraryApi = new _LibraryApi(config);
export const MentorshipsApi = new _MentorshipsApi(config);
export const ModerationApi = new _ModerationApi(config);
export const NotificationsApi = new _NotificationsApi(config);
export const OrganizationsApi = new _OrganizationsApi(config);
export const SearchApi = new _SearchApi(config);
export const RedirectsApi = new _RedirectsApi(config);
export const UsersApi = new _UsersApi(config);
export const XBlocksApi = new _XblocksApi(config);

if (process.env.NODE_ENV === 'development') {
    // For development and debugging purposes, expose the API client to the browser console.
    (window as any).AnalyticsApi = AnalyticsApi;
    (window as any).BannersApi = BannersApi;
    (window as any).ClassroomsApi = ClassroomsApi;
    (window as any).ClustersApi = ClustersApi;
    (window as any).DiscussionsApi = DiscussionsApi;
    (window as any).FavoritesApi = FavoritesApi;
    (window as any).ItemsApi = ItemsApi;
    (window as any).LearningApi = LearningApi;
    (window as any).LibraryApi = LibraryApi;
    (window as any).MentorshipsApi = MentorshipsApi;
    (window as any).ModerationApi = ModerationApi;
    (window as any).NotificationsApi = NotificationsApi;
    (window as any).OrganizationsApi = OrganizationsApi;
    (window as any).SearchApi = SearchApi;
    (window as any).RedirectsApi = RedirectsApi;
    (window as any).UsersApi = UsersApi;
    (window as any).XBlocksApi = XBlocksApi;
}
